import { convertToRem } from '../../utils/pixel-to-rem-convertor.js';
import { convertToPixel } from '../../utils/rem-to-pixel-convertor.js';

class Point {
  /**
   *
   * @param {Array<Number>} point
   * @param {Number} rotation
   */
  constructor(point, rotation = 0) {
    this.x = point[0];
    this.y = point[1];
    this.rotation = rotation;
  }
}
// returns position of arrow wrt popover
export class ArrowPosition {
  constructor(target, popover) {
    this.arrowPositionX = 0;
    this.arrowPositionY = 0;
    this.arrowRotation = 0;
    this.setArrowPosition(target, popover);
  }
  setArrowPosition(target, popover) {
    this.arrowX0 = -0.375; // the starting reference point of arrow in x direction- give this as left value in rem to see the position.
    this.arrowY0 = -0.875; // the starting reference point of arrow in y direction- give this as top value in rem to see the position.
    this.arrowRotation0 = -270;
    // correcting the target so as to accurately track the icon of the
    // workline type information.
    if (target.tagName === 'SH-WORKLINE-ITEM' && target.type === 'information') {
      target = target.shadowRoot.querySelector('sh-icon');
    }
    this.targetRect = target.getBoundingClientRect();
    this.popoverRect = popover.getBoundingClientRect();
    // the half-width of the upright triangle from one end to its midpoint in the base
    this.arrowBaseHalfWidth = convertToPixel(0.375);
    // the distance between the triangle and the popover edge.
    this.arrowPopoverEdgeBoundaryDistance = convertToPixel(0.5);

    this.targetLeft = this.targetRect.left;
    this.targetRight = this.targetRect.right;
    this.targetTop = this.targetRect.top;
    this.targetBottom = this.targetRect.bottom;

    this.popoverLeft = this.popoverRect.left;
    this.popoverRight = this.popoverRect.right;
    this.popoverTop = this.popoverRect.top;
    this.popoverBottom = this.popoverRect.bottom;

    this.rangeCorrectedPopoverLeft =
      this.popoverLeft + (this.arrowBaseHalfWidth + this.arrowPopoverEdgeBoundaryDistance);
    this.rangeCorrectedPopoverRight =
      this.popoverRight - (this.arrowBaseHalfWidth + this.arrowPopoverEdgeBoundaryDistance);
    this.rangeCorrectedPopoverTop =
      this.popoverTop + (this.arrowBaseHalfWidth + this.arrowPopoverEdgeBoundaryDistance);
    this.rangeCorrectedPopoverBottom =
      this.popoverBottom - (this.arrowBaseHalfWidth + this.arrowPopoverEdgeBoundaryDistance);

    this.yIntersection = this.getIntersectionExtremes(
      this.targetLeft,
      this.targetRight,
      this.popoverLeft,
      this.popoverRight
    );
    this.xIntersection = this.getIntersectionExtremes(
      this.targetTop,
      this.targetBottom,
      this.popoverTop,
      this.popoverBottom
    );
    if (this.yIntersection.length > 0) {
      let xMid = (this.yIntersection[0] + this.yIntersection[this.yIntersection.length - 1]) / 2;
      xMid =
        xMid > this.rangeCorrectedPopoverRight
          ? this.rangeCorrectedPopoverRight
          : xMid < this.rangeCorrectedPopoverLeft
          ? this.rangeCorrectedPopoverLeft
          : xMid;
      let y, rotation;
      if (this.targetTop < this.popoverTop) {
        y = this.popoverTop;
        rotation = 90;
      } else if (this.targetBottom > this.popoverBottom) {
        y = this.popoverBottom;
        rotation = 270;
      }
      this.setArrowParameters(rotation, xMid, y);
    } else if (this.xIntersection.length > 0) {
      let yMid = (this.xIntersection[0] + this.xIntersection[this.xIntersection.length - 1]) / 2;
      yMid =
        yMid > this.rangeCorrectedPopoverBottom
          ? this.rangeCorrectedPopoverBottom
          : yMid < this.rangeCorrectedPopoverTop
          ? this.rangeCorrectedPopoverTop
          : yMid;
      let x, rotation;
      if (this.targetLeft < this.popoverLeft) {
        x = this.popoverLeft;
        rotation = 180;
      } else if (this.targetRight > this.popoverRight) {
        x = this.popoverRight;
        rotation = 0;
      }
      this.setArrowParameters(rotation, x, yMid);
    }
    // no Intersection with any side...
    else if (this.xIntersection.length === 0 && this.yIntersection.length === 0) {
      let specialPoint;
      if (this.targetLeft > this.popoverRight) {
        if (this.targetTop > this.popoverBottom) {
          specialPoint = this.getSpecialPoint('right-down');
        } else if (this.targetBottom < this.popoverTop) {
          specialPoint = this.getSpecialPoint('right-up');
        }
      } else if (this.targetRight < this.popoverLeft) {
        if (this.targetTop > this.popoverBottom) {
          specialPoint = this.getSpecialPoint('left-down');
        } else if (this.targetBottom < this.popoverTop) {
          specialPoint = this.getSpecialPoint('left-up');
        }
      }
      this.setArrowParameters(specialPoint.rotation, specialPoint.x, specialPoint.y);
    }
  }
  convertAbsoluteValuesToRelativeValues(rotation, x, y) {
    rotation = this.arrowRotation0 - rotation;
    x = convertToRem(x - this.popoverRect.left) + this.arrowX0;
    y = convertToRem(y - this.popoverRect.top) + this.arrowY0;
    return { rotation, x, y };
  }
  setArrowParameters(rotation, x, y) {
    const arrowParameters = this.convertAbsoluteValuesToRelativeValues(rotation, x, y);
    this.arrowPositionX = arrowParameters.x;
    this.arrowPositionY = arrowParameters.y;
    this.arrowRotation = arrowParameters.rotation;
  }
  /**
   *
   * @param {DOMRect} rect
   * @param {String} position
   * @returns {Point}
   */
  getSpecialPoint(position) {
    const specialPointData = {
      'left-up': new Point([this.popoverLeft, this.rangeCorrectedPopoverTop], 180),
      'left-down': new Point([this.popoverLeft, this.rangeCorrectedPopoverBottom], 180),
      'right-up': new Point([this.popoverRight, this.rangeCorrectedPopoverTop], 0),
      'right-down': new Point([this.popoverRight, this.rangeCorrectedPopoverBottom], 0),
    };
    return specialPointData[position];
  }
  getIntersectionExtremes(t1, t2, p1, p2) {
    const intersectingPoints = [];
    for (let i = t1; i <= t2; i++) {
      if (i >= p1 && i <= p2) {
        intersectingPoints.push(i);
      }
    }
    return intersectingPoints;
  }
}
